import { useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import * as grid from '@campgladiator/cgui-core.styles.helpers.grid'
import { PageTitle } from '../../common/components/PageTitle'
import { testIds, testIdAttribute } from '../../common/utility/labels'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { trackFavoriteEvents } from '../../common/utility/commonAnalytics'
import { useDeviceWidth, useScrolledToRefBottom } from '../../common/hooks'
import { authSelector } from '../../redux/features/Auth'
import {
  favorite,
  favoriteSelector,
} from '../../redux/features/OnDemand/Favorite'
import {
  filtersSelector,
  getFilters,
  setFavorite,
  setFilters,
} from '../../redux/features/OnDemand/Filters'
import {
  getWorkouts,
  workoutsSelector,
  workoutsReset,
} from '../../redux/features/OnDemand/Workouts'
import { Filters } from './components/Filters'
import { WorkoutsGrid } from './components/WorkoutsGrid'
import { Skeleton } from './components/Skeleton'
import styles from './OnDemandWorkouts.module.scss'

const OnDemandWorkouts = () => {
  const { isLoggedIn } = useAppSelector(authSelector)

  const [isPageInitialized, setIsPageInitialized] = useState(false)

  const workoutsGridRef = useRef<HTMLDivElement>(null)

  const workoutsGridLoadNewPageOffsetInPx = -100

  const scrolledToWorkoutsGridBottom = useScrolledToRefBottom(
    workoutsGridRef,
    workoutsGridLoadNewPageOffsetInPx,
  )

  const {
    filters,
    tagFilters: tagFiltersApplied,
    favoriteFilter: isFavoriteFilterApplied,
    hideCompletedFilter: isHideCompletedFilterApplied,
    isLoading: isLoadingFilters,
  } = useAppSelector(filtersSelector)

  const {
    isLoading: isLoadingWorkouts,
    lastPageFetched,
    totalNumberOfPages,
    totalNumberOfWorkouts,
    workouts,
  } = useAppSelector(workoutsSelector)

  const isMorePagesToFetch =
    (typeof lastPageFetched === 'undefined' ? -1 : lastPageFetched) <
    totalNumberOfPages - 1

  const allFavorites = useAppSelector(favoriteSelector.selectEntities)

  const dispatch = useAppDispatch()
  const deviceWidth = useDeviceWidth()
  const [isOverlayOn, setIsOverlayOn] = useState(false)
  const handleOnGetFilteredWorkouts = (
    tagFilterIds: number[],
    hideCompleted: boolean,
  ) => {
    dispatch(workoutsReset())
    dispatch(setFilters(tagFilterIds, hideCompleted))
  }

  const handleOnFavoriteClick = (workoutId: number, isFavorite: boolean) => {
    const favoriteParams = { workoutId }
    trackFavoriteEvents(isFavorite)
    dispatch(favorite(favoriteParams))
  }

  const handleOnGetFavoritesWorkouts = () => {
    dispatch(workoutsReset())
    dispatch(setFavorite(!isFavoriteFilterApplied))
  }

  const overlayTestId = testIdAttribute(testIds.WORKOUTS.OVERLAY)

  useEffect(() => {
    dispatch(workoutsReset())
    dispatch(getWorkouts())
  }, [
    dispatch,
    tagFiltersApplied,
    isFavoriteFilterApplied,
    isHideCompletedFilterApplied,
  ])

  useEffect(() => {
    dispatch(getFilters())
    setIsPageInitialized(true)
  }, [dispatch])

  const loadMoreWorkouts = useCallback(() => {
    !isLoadingWorkouts && isMorePagesToFetch && dispatch(getWorkouts())
  }, [dispatch, isLoadingWorkouts, isMorePagesToFetch])

  useEffect(() => {
    scrolledToWorkoutsGridBottom && loadMoreWorkouts()
  }, [scrolledToWorkoutsGridBottom, loadMoreWorkouts])

  return (
    <>
      {!isPageInitialized ? (
        <Skeleton />
      ) : (
        <>
          <div className={classNames(grid.container, styles.content)}>
            <PageTitle title="On-Demand Workouts">
              Workouts at your convenience led by our top certified personal
              trainers.
            </PageTitle>
            <Filters
              tagFiltersApplied={tagFiltersApplied}
              filters={filters}
              onOverlayIsSet={setIsOverlayOn}
              isLoadingWorkouts={isLoadingWorkouts}
              isLoadingFilters={isLoadingFilters}
              deviceWidth={deviceWidth}
              isFavoriteFilterApplied={isFavoriteFilterApplied}
              isLoggedIn={isLoggedIn}
              onGetFilteredWorkouts={handleOnGetFilteredWorkouts}
              onGetFavoriteWorkouts={handleOnGetFavoritesWorkouts}
              isHideCompletedFilterApplied={isHideCompletedFilterApplied}
            />
            <WorkoutsGrid
              ref={workoutsGridRef}
              isFiltered={
                tagFiltersApplied.length > 0 || isHideCompletedFilterApplied
              }
              isLoading={isLoadingWorkouts}
              allFavorites={allFavorites}
              isLoggedIn={isLoggedIn}
              onFavoriteClick={(workoutId, isFavorite) =>
                handleOnFavoriteClick(workoutId, isFavorite)
              }
              isFavoriteFilterApplied={isFavoriteFilterApplied}
              totalNumberOfWorkouts={totalNumberOfWorkouts}
              workouts={workouts}
            />
          </div>
          {isOverlayOn && (
            <div className={styles.overlay} {...overlayTestId}></div>
          )}
        </>
      )}
    </>
  )
}

export default OnDemandWorkouts
